// 品質管理一覧表 (イベント単位) — 発注者提出系
// ヘッダ部に合否判定利用値(規格値)、本体に工程実施毎の温湿度・配合・撹拌・可使時間の合否。

import ExcelJS from "exceljs";
import type { ExtendedDB } from "../../../../types/extendedDB";
import { deriveQualityMeasurements } from "../../../../types/derivedSelectors";
import {
  buildLookups, downloadBlob, todayStr, setHeaderRow,
  fmtDate, fmtDateTime, diffMinutes, buildMixRatioRecipe,
  findPotLifeMinutes, round1, processAreaIdOf,
} from "./helpers";

function resolveTargetWork(db: ExtendedDB, lk: ReturnType<typeof buildLookups>, workId?: number) {
  if (workId != null) {
    return lk.workById.get(workId);
  }
  return db.works.find((w) => w.deleted_at == null);
}

function judgeStirring(mixDef: { stirring_time_minutes: number } | undefined, stirringMins: number | undefined): string {
  if (mixDef && stirringMins != null && stirringMins >= mixDef.stirring_time_minutes) return "○";
  if (stirringMins != null) return "×";
  return "-";
}

function judgePotLife(potLimit: number | undefined, potLifeUsed: number | undefined): string {
  if (potLimit != null && potLifeUsed != null) {
    return potLifeUsed <= potLimit ? "○" : "×";
  }
  return "-";
}

function judgeTemperature(ambientTemperature: number | null | undefined): string {
  return ambientTemperature != null ? "○" : "-";
}

function judgeHumidity(humidityUpper: number | null | undefined, ambientHumidity: number | null | undefined): string {
  if (humidityUpper != null && ambientHumidity != null) {
    return ambientHumidity <= humidityUpper ? "○" : "×";
  }
  if (ambientHumidity != null) return "○";
  return "-";
}

function judgeTempHumid(tempJudge: string, humidJudge: string): string {
  if (tempJudge === "○" && humidJudge === "○") return "○";
  if (humidJudge === "×") return "×";
  return "-";
}

function resolveMixDef(mixDefId: number | null | undefined, lk: ReturnType<typeof buildLookups>) {
  if (mixDefId != null) return lk.mixRatioDefById.get(mixDefId);
  return undefined;
}

function resolveMixMaterial(mixDef: { material_id: number } | undefined, lk: ReturnType<typeof buildLookups>) {
  if (mixDef) return lk.materialById.get(mixDef.material_id);
  return undefined;
}

function resolvePotLimit(material: { id: number } | undefined, ambientTemperature: Parameters<typeof findPotLifeMinutes>[1], lk: ReturnType<typeof buildLookups>): number | undefined {
  if (material) return findPotLifeMinutes(material.id, ambientTemperature, lk);
  return undefined;
}

// QualityRecord に spot_id 相当のフィールドが無いため、kind と construction_id で抽出する。
// (旧モックの spot_id フィルタは派生値が常に undefined となり結果が空になっていた)
// kind のみで絞ると全工程実施行が DB 先頭の配合実績を共有してしまうため、対象施工の行のみに絞り込む。
function selectMixActuals(qualityMeasurements: ReturnType<typeof deriveQualityMeasurements>, constructionId: string | undefined) {
  if (constructionId == null) {
    return [];
  }
  return qualityMeasurements.filter((q) => q.measurement_kind === "mix_actual" && q.construction_id === constructionId);
}

function buildMixRecipeText(hasMixActual: boolean, mixDef: { id: number } | undefined, lk: ReturnType<typeof buildLookups>): string {
  if (hasMixActual && mixDef) return buildMixRatioRecipe(mixDef.id, lk).recipe;
  return "";
}

function judgeMix(mixRecipeText: string): string {
  return mixRecipeText ? "○" : "-";
}

function roundOrBlank(value: number | string): number | "" {
  if (typeof value === "number") return round1(value);
  return "";
}

// number(epoch) → ISO 文字列。fmtDateTime/diffMinutes は文字列前提のため変換が必要。
function epochToIso(v: number | null | undefined): string | undefined {
  if (v == null) return undefined;
  return new Date(v).toISOString();
}

export async function exportQualityList(db: ExtendedDB, workId?: number): Promise<void> {
  const lk = buildLookups(db);
  const work = resolveTargetWork(db, lk, workId);
  const qualityMeasurements = deriveQualityMeasurements(db);

  const wb = new ExcelJS.Workbook();
  wb.creator = "奥村組 トレーサビリティシステム";
  const ws = wb.addWorksheet("品質管理一覧表");

  ws.getCell("A1").value = "品質管理一覧表";
  ws.getCell("A1").font = { bold: true, size: 14 };
  if (work) {
    ws.getCell("A3").value = "工事名:";   ws.getCell("A3").font = { bold: true }; ws.getCell("B3").value = work.official_name;
    ws.getCell("A4").value = "工事場所:"; ws.getCell("A4").font = { bold: true }; ws.getCell("B4").value = work.location;
    ws.getCell("A5").value = "出力日:";   ws.getCell("A5").font = { bold: true }; ws.getCell("B5").value = todayStr();
  }
  ws.getCell("A7").value = "合否判定利用値";
  ws.getCell("A7").font = { bold: true };

  const relevantMaterialIds = new Set<number>();
  if (work) {
    for (const c of db.carryIns.filter((c) => c.deleted_at == null)) {
      if (c.work_id !== work.id) continue;
      const qrs = db.materialQrs.filter((q) => q.carry_in_id === c.id);
      for (const q of qrs) {
        if (!q.lot_id) continue;
        const lot = lk.lotById.get(q.lot_id);
        if (lot && lot.material_id != null) relevantMaterialIds.add(lot.material_id);
      }
    }
    for (const area of db.constructionPlanAreas.filter((a) => a.deleted_at == null)) {
      if (area.work_id !== work.id) continue;
      const wds = db.workDetails.filter((d) => d.deleted_at == null && d.work_subtype_id === area.work_subtype_id);
      for (const wd of wds) {
        if (wd.mix_ratio_definition_id != null) {
          const def = lk.mixRatioDefById.get(wd.mix_ratio_definition_id);
          if (def) relevantMaterialIds.add(def.material_id);
        }
      }
    }
  }

  let r = 8;
  for (const m of db.materials) {
    if (m.deleted_at != null || !m.qr_managed) continue;
    if (work && relevantMaterialIds.size > 0 && !relevantMaterialIds.has(m.id)) continue;
    const potLifeSpecs = (lk.potLifeSpecsByMaterialId.get(m.id) ?? []).filter((s) => s.deleted_at == null);
    if (potLifeSpecs.length === 0 && m.humidity_upper == null && m.humidity_lower == null) continue;
    ws.getCell(r, 1).value = `材料: ${m.name}`;
    ws.getCell(r, 1).font = { bold: true };
    r++;
    if (potLifeSpecs.length > 0) {
      const lower = Math.min(...potLifeSpecs.map((s) => s.temperature_lower ?? Infinity));
      const upper = Math.max(...potLifeSpecs.map((s) => s.temperature_upper ?? -Infinity));
      ws.getCell(r, 1).value = "  温度下限:";
      ws.getCell(r, 2).value = isFinite(lower) ? `${lower}℃以上` : "-";
      ws.getCell(r, 3).value = "管理値";
      r++;
      ws.getCell(r, 1).value = "  温度上限:";
      ws.getCell(r, 2).value = isFinite(upper) ? `${upper}℃以下` : "-";
      ws.getCell(r, 3).value = "管理値";
      r++;
    }
    ws.getCell(r, 1).value = "  湿度下限:";
    ws.getCell(r, 2).value = m.humidity_lower != null ? `${m.humidity_lower}%以上` : "-";
    r++;
    ws.getCell(r, 1).value = "  湿度上限:";
    ws.getCell(r, 2).value = m.humidity_upper != null ? `${m.humidity_upper}%未満` : "-";
    if (m.humidity_upper != null) ws.getCell(r, 3).value = "管理値";
    r++;
    const mixDef = db.mixRatioDefinitions.find((d) => d.deleted_at == null && d.material_id === m.id);
    if (mixDef) {
      const recipe = buildMixRatioRecipe(mixDef.id, lk);
      ws.getCell(r, 1).value = "  配合比:";
      ws.getCell(r, 2).value = recipe.recipe;
      ws.getCell(r, 3).value = "管理値";
      r++;
      if (recipe.stirringTime != null) {
        ws.getCell(r, 1).value = "  撹拌時間:";
        ws.getCell(r, 2).value = `${recipe.stirringTime}分以上`;
        ws.getCell(r, 3).value = "管理値";
        r++;
      }
    }
    const potMin = potLifeSpecs.map((s) => s.pot_life_minutes).filter((x): x is number => x != null);
    if (potMin.length > 0) {
      ws.getCell(r, 1).value = "  可使時間:";
      ws.getCell(r, 2).value = `${Math.min(...potMin)}〜${Math.max(...potMin)}分以内`;
      ws.getCell(r, 3).value = "管理値";
      r++;
    }
    r++;
  }

  r++;
  ws.getCell(r, 1).value = "※ 「施工完了時刻」「攪拌時刻」のカッコ内の時刻は、登録時に未入力のためシステムが算出した参考時刻です。";
  ws.getCell(r, 1).font = { italic: true, size: 10 };
  r += 2;

  const headerRowIdx = r;
  const headers: Array<[string, number]> = [
    ["施工日", 12],
    ["区分名6", 12], ["区分名7", 12], ["区分名8", 12], ["区分名9", 12], ["区分名10", 12],
    ["気温(℃)", 10], ["合否", 6],
    ["湿度(%)", 10], ["合否", 6],
    ["温度湿度合否", 12],
    ["主材料(kg)", 12], ["副材料(kg)", 12], ["合否", 6],
    ["調合開始", 18], ["調合完了", 18], ["撹拌時間(分)", 12], ["合否", 6],
    ["施工完了", 18], ["可使時間(分)", 12], ["合否", 6],
    ["備考(情報)", 22], ["備考(実績)", 22],
    ["写真(施工)", 10], ["施工画面リンク", 30],
  ];
  headers.forEach((h, i) => {
    ws.getCell(headerRowIdx, i + 1).value = h[0];
    ws.getColumn(i + 1).width = h[1];
  });
  setHeaderRow(ws, headerRowIdx);
  r = headerRowIdx + 1;

  const processes = db.processExecutions.filter((p) => p.deleted_at == null).sort((a, b) => a.createdAt - b.createdAt);
  for (const p of processes) {
    const construction = lk.constructionById.get(p.construction_id);
    if (!construction) continue;
    const areaId = processAreaIdOf(p);
    if (work && db.constructionPlanAreas.find((a) => a.deleted_at == null && a.id === areaId)?.work_id !== work.id) continue;

    const pArea = areaId != null ? lk.planAreaById.get(areaId) : undefined;
    // 区分名6〜10 は API に直接フィールド無し。"-" で埋める。
    const slot6 = ""; const slot7 = ""; const slot8 = ""; const slot9 = ""; const slot10 = "";

    const stir = lk.stirByProcessId.get(p.id);
    const wk = lk.workExecByProcessId.get(p.id);

    const mixDef2 = resolveMixDef(stir?.mix_ratio_definition_id, lk);
    const material = resolveMixMaterial(mixDef2, lk);

    const stirringMins = diffMinutes(stir?.stir_started_at, stir?.stir_finished_at);
    const stirJudge = judgeStirring(mixDef2, stirringMins);

    const potLifeFromMix = stir?.stir_finished_at ?? stir?.stir_started_at;
    const potLifeUsed = diffMinutes(potLifeFromMix, wk?.construction_finished_at ?? epochToIso(construction.finished_at));
    const potLimit = resolvePotLimit(material, construction.ambient_temperature ?? undefined, lk);
    const potJudge = judgePotLife(potLimit, potLifeUsed);

    const tempJudge = judgeTemperature(construction.ambient_temperature);
    const humidJudge = judgeHumidity(material?.humidity_upper, construction.ambient_humidity);
    const tempHumidJudge = judgeTempHumid(tempJudge, humidJudge);

    const qms = selectMixActuals(qualityMeasurements, construction?.id);
    const mixActual = qms[0];
    const mainKg = mixActual?.mix_actual_main ?? "";
    const subKg = mixActual?.mix_actual_sub ?? "";
    const mixRecipeText = buildMixRecipeText(mixActual != null, mixDef2, lk);
    const mixJudge: string = judgeMix(mixRecipeText);

    const mainKgRounded = roundOrBlank(mainKg);
    const subKgRounded = roundOrBlank(subKg);
    const values = [
      fmtDate(construction.executed_at),
      slot6, slot7, slot8, slot9, slot10,
      round1(construction.ambient_temperature), tempJudge,
      round1(construction.ambient_humidity), humidJudge,
      tempHumidJudge,
      mainKgRounded, subKgRounded, mixJudge,
      fmtDateTime(stir?.stir_started_at), fmtDateTime(stir?.stir_finished_at), stirringMins ?? "", stirJudge,
      fmtDateTime(wk?.construction_finished_at ?? epochToIso(construction.finished_at)), potLifeUsed ?? "", potJudge,
      pArea?.remarks ?? "",
      p.remarks ?? construction.remarks ?? "",
      "-",
      construction ? `/console/construction-execute/${construction.id}/` : "",
    ];
    values.forEach((v, i) => { ws.getCell(r, i + 1).value = v ?? ""; });
    r++;
  }

  if (processes.length === 0) {
    ws.getCell(r, 1).value = "※ 工程実施データが存在しません";
    ws.getCell(r, 1).font = { italic: true };
  }

  const buffer = await wb.xlsx.writeBuffer();
  downloadBlob(buffer, `品質管理一覧表_${todayStr()}.xlsx`);
}
